import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Chip,
  Avatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  Alert
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Assignment as AssignmentIcon,
  HealthAndSafety as HealthIcon,
  School as SchoolIcon,
  FamilyRestroom as FamilyIcon,
  VerifiedUser as VerifiedUserIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  InsertDriveFile as FileIcon,
  Visibility as VisibilityIcon
  ,PhotoCamera as PhotoCameraIcon,
  Assessment as AssessmentIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import dayjs from 'dayjs';

// City Skyline SVG Component for decoration
const CitySkyline = () => (
  <Box
    sx={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: '200px',
      zIndex: -1,
      opacity: 0.1,
      pointerEvents: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%230099ff' fill-opacity='1' d='M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'bottom'
    }}
  />
);

// Updated document types based on new specification (parentheses removed from labels).
// Required flags kept only for items explicitly marked (Wajib) in provided list.
const documentTypes = [
  { id: 26, label: 'Formulir Biodata Pelamar Kerja', icon: <AssignmentIcon />, required: true, },
  { id: 1, label: 'Surat Lamaran', icon: <AssignmentIcon />, required: false },
  { id: 2, label: 'FC KTP', icon: <BadgeIcon />, required: false },
  { id: 3, label: 'FC SIM', icon: <BadgeIcon />, required: false },
  { id: 23, label: 'Foto Full Body', icon: <PhotoCameraIcon />, required: true },
  { id: 4, label: 'Pas Foto 4X6', icon: <AssignmentIcon />, required: false },
  { id: 24, label: 'Hasil Test MTK', icon: <AssessmentIcon />, required: true },
  { id: 5, label: 'FC BPJS Kesehatan', icon: <HealthIcon />, required: false },
  { id: 6, label: 'FC BPJS Ketenagakerjaan', icon: <HealthIcon />, required: false },
  { id: 7, label: 'Sertifikat Vaksin 1 & 2', icon: <HealthIcon />, required: false },
  { id: 8, label: 'FC NPWP', icon: <AssignmentIcon />, required: false },
  { id: 9, label: 'FC Rekening Mandiri', icon: <AssignmentIcon />, required: true },
  { id: 10, label: 'FC Akte Lahir', icon: <FamilyIcon />, required: false },
  { id: 11, label: 'FC Akte Nikah', icon: <FamilyIcon />, required: false },
  { id: 12, label: 'Surat Kesehatan', icon: <HealthIcon />, required: true },
  { id: 13, label: 'FC Ijazah', icon: <SchoolIcon />, required: true },
  { id: 14, label: 'Transkrip Nilai', icon: <SchoolIcon />, required: true },
  { id: 15, label: 'FC Sertifikat', icon: <VerifiedUserIcon />, required: false },
  { id: 16, label: 'Surat Keterangan Kerja', icon: <BusinessIcon />, required: false },
  { id: 17, label: 'FC SKCK', icon: <VerifiedUserIcon />, required: true },
  { id: 18, label: 'Surat Pakta Integritas', icon: <DescriptionIcon />, required: false },
  { id: 19, label: 'Surat Jaminan', icon: <DescriptionIcon />, required: false },
  { id: 20, label: 'FC KTP Ayah', icon: <FamilyIcon />, required: false },
  { id: 21, label: 'FC Kartu Keluarga', icon: <FamilyIcon />, required: false },
  { id: 22, label: 'Hasil Tes DISC', icon: <AssignmentIcon />, required: false, link: 'https://tes.anthonykusuma.com/disc/tes' },
  { id: 25, label: 'FC KTP Penjamin', icon: <FamilyIcon />, required: true }
];

const PublicEmployeeDocs = () => {
  const { id } = useParams();
  // Raw id from URL may include suffix or whitespace
  const rawId = (id || '').trim();
  // Extract base employee _id (URL may contain suffix like _timestamp_random)
  const baseEmployeeId = rawId.split('_')[0];
  const isValidObjectId = /^[a-fA-F0-9]{24}$/.test(baseEmployeeId);
  const debugMode = typeof window !== 'undefined' && window.location.search.includes('debug');
  if (debugMode) {
    // eslint-disable-next-line no-console
    console.log('[PublicEmployeeDocs] rawId:', rawId, 'baseEmployeeId:', baseEmployeeId, 'isValidObjectId:', isValidObjectId);
  }
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({}); // 'success', 'error', 'uploading'
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '', title: '' });
  const [existingDocs, setExistingDocs] = useState([]);
  const [showExistingDocs, setShowExistingDocs] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customFilesState, setCustomFilesState] = useState([{ name: '', file: null }]);
  const [tokenRemainingPublic, setTokenRemainingPublic] = useState('');
  // Keep a modifiable copy of the public token from the URL so we can regenerate it client-side
  const [currentPublicToken, setCurrentPublicToken] = useState(rawId);

  // Sanitize label: remove parentheses content and extra spaces.
  const sanitizeLabel = (label) => (label || '')
    .replace(/\([^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Resolve a document record into a viewable URL. Supports several potential backend field names.
  const resolveDocUrl = (doc) => {
    if (!doc || typeof doc !== 'object') return null;
    // Prefer gsUri if present
    const candidates = [doc.gsUri, doc.url, doc.file_url, doc.fileUrl, doc.gcsUri];
    for (const c of candidates) {
      if (c && typeof c === 'string') {
        if (c.startsWith('gs://')) {
          // Convert GCS URI to public storage URL (assuming objects are publicly accessible or signed elsewhere)
          const parts = c.replace('gs://', '').split('/');
            const bucket = parts.shift();
            return `https://storage.googleapis.com/${bucket}/${parts.join('/')}`;
        }
        return c;
      }
    }
    return null;
  };

  // Open a document by matching its sanitized label to existingDocs
  const openDoc = (label) => {
    const target = sanitizeLabel(label).toLowerCase();
    const found = existingDocs.find(d => {
      const docName = sanitizeLabel(d.document_name || '').toLowerCase();
      return docName.includes(target) || target.includes(docName);
    });
    if (!found) {
      Swal.fire({
        ...SwalConfig.info,
        title: 'Dokumen Tidak Ditemukan',
        text: `Tidak ada dokumen untuk ${label}.`
      });
      return;
    }
    const url = resolveDocUrl(found);
    if (!url) {
      Swal.fire({
        ...SwalConfig.info,
        title: 'URL Tidak Tersedia',
        text: 'Dokumen ada tetapi URL tidak diberikan oleh backend.'
      });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      if (!isValidObjectId) {
        setEmployee(null);
        setLoading(false);
        setErrorDialog({
          open: true,
          title: 'Link Tidak Valid',
          message: 'ID karyawan pada link tidak sesuai format. Minta link baru dari HRD.\nFormat yang benar: 24 karakter hex (contoh: 681c1cb846cfbc15421a1296).',
          type: 'error'
        });
        return;
      }
      try {
        const response = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${baseEmployeeId}`);
        if (!response.ok) {
          let reason = 'Terjadi kesalahan.';
          if (response.status === 404) reason = 'Data karyawan tidak ditemukan (404).';
          else if (response.status === 401) reason = 'Tidak memiliki akses (401). Cek apakah endpoint memerlukan token.';
          else if (response.status === 403) reason = 'Akses ditolak (403).';
          else if (response.status >= 500) reason = 'Server sedang mengalami gangguan.';
          setErrorDialog({
            open: true,
            title: 'Link Tidak Valid',
            message: `Link tidak dapat digunakan. ${reason}\nSilakan minta link baru dari HRD.`,
            type: 'error'
          });
          throw new Error(reason);
        }
        const data = await response.json();
        setEmployee(data);
        
        // Set existing documents
        if (data.documents && Array.isArray(data.documents)) {
          setExistingDocs(data.documents);
          if (data.documents.length > 0) {
            setShowExistingDocs(true); // auto expand if there are documents
          }
        }
        // Also try to merge files that exist in GCS (even if backend didn't create document records)
        try {
          const folderName = `${data.full_name || 'Unknown'}-${data.emp_id || baseEmployeeId}`;
          const listResp = await fetch(`https://apidashboard.eurekagroup.id/documents/list-gcs?folder=${encodeURIComponent(folderName)}`);
          if (listResp.ok) {
            const listJson = await listResp.json().catch(() => null);
            if (listJson && Array.isArray(listJson.items) && listJson.items.length > 0) {
              // Map GCS items to our known document types using token overlap
              const toAdd = [];
              listJson.items.forEach(item => {
                const fnNorm = normalize(item.filename || '');
                const fnTokens = fnNorm.split(' ').filter(Boolean);
                // find a documentType with token overlap
                const matched = documentTypes.find(dt => {
                  const dtTokens = normalize(sanitizeLabel(dt.label)).split(' ').filter(Boolean);
                  return dtTokens.some(t => fnTokens.includes(t));
                });
                if (matched) {
                  const sanitizedLabel = sanitizeLabel(matched.label);
                  toAdd.push({
                    document_name: sanitizedLabel,
                    filename: item.filename,
                    folder: listJson.folder || folderName,
                    gsUri: item.gsUri,
                    date_added: dayjs(item.created).format('YYYY-MM-DD'),
                    document_code: Math.random().toString(36).slice(-8),
                    category_name: sanitizedLabel,
                    full_name: data?.full_name || 'User'
                  });
                }
              });
              if (toAdd.length > 0) {
                setExistingDocs(prev => {
                  const updated = [...prev];
                  toAdd.forEach(newDoc => {
                    const idx = updated.findIndex(d => normalize(sanitizeLabel(d.document_name || '')) === normalize(newDoc.document_name));
                    if (idx >= 0) {
                      updated[idx] = { ...updated[idx], ...newDoc };
                    } else {
                      updated.push(newDoc);
                    }
                  });
                  return updated;
                });
                setShowExistingDocs(true);
                if (debugMode) console.debug('[PublicEmployeeDocs] merged GCS -> toAdd:', toAdd, 'existingDocs now approx length:', (existingDocs || []).length + toAdd.length);
              }
            }
          }
        } catch (e) {
          if (debugMode) console.log('list-gcs merge error', e);
        }
      } catch (error) {
        console.error('Error fetching employee:', error);
        if (!errorDialog.open) {
          Swal.fire({
            ...SwalConfig.error,
            title: 'Error',
            text: 'Data karyawan tidak ditemukan atau terjadi kesalahan koneksi.'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchEmployee();
  }, [id, baseEmployeeId]);

  // Debug progress values when debugMode is enabled
  useEffect(() => {
    if (!debugMode) return;
    console.debug('[PublicEmployeeDocs] debug progress', {
      documentTypesCount: documentTypes.length,
      expectedTotal,
      uploadedCount,
      existingDocsLength: existingDocs.length,
      uploadStatus
    });
  }, [debugMode, expectedTotal, uploadedCount, existingDocs, uploadStatus]);

  // Compute remaining time when public link includes timestamp (format: <employeeId>_<timestamp>_<random>)
  useEffect(() => {
    if (!currentPublicToken) {
      setTokenRemainingPublic('');
      return;
    }
    const parts = currentPublicToken.split('_');
    if (parts.length < 2) {
      setTokenRemainingPublic('');
      return;
    }
    const ts = Number(parts[1]);
    if (!ts || Number.isNaN(ts)) {
      setTokenRemainingPublic('');
      return;
    }

    // public link expiry duration in milliseconds (72 hours = 3 days)
    const EXPIRY_MS = 72 * 60 * 60 * 1000;

    const update = () => {
      const expiry = ts + EXPIRY_MS;
      const diff = expiry - Date.now();
      if (diff <= 0) {
        setTokenRemainingPublic('Sudah kadaluwarsa');
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTokenRemainingPublic(`${hours} jam ${minutes} menit`);
    };

    update();
    const iv = setInterval(update, 30000);
    return () => clearInterval(iv);
  }, [currentPublicToken]);

  // Derived boolean: whether the (current) public link has expired
  const isPublicLinkExpired = React.useMemo(() => {
    if (!currentPublicToken) return true;
    const parts = currentPublicToken.split('_');
    if (parts.length < 2) return true;
    const ts = Number(parts[1]);
    if (!ts || Number.isNaN(ts)) return true;
    const EXPIRY_MS = 72 * 60 * 60 * 1000;
    return (ts + EXPIRY_MS) - Date.now() <= 0;
  }, [currentPublicToken]);

  // Regenerate a new public token for this employee (frontend-only token generator)
  const generateNewPublicToken = (opts = { replaceUrl: true, copy: true }) => {
    const newToken = `${baseEmployeeId}_${Date.now()}_${Math.random().toString(36).slice(2,11)}`;
    setCurrentPublicToken(newToken);
    // update the browser URL if the rawId appears in the pathname
    try {
      if (opts.replaceUrl && typeof window !== 'undefined') {
        const newPath = window.location.pathname.includes(rawId)
          ? window.location.pathname.replace(rawId, newToken)
          : `${window.location.pathname.replace(/\/$/, '')}/${newToken}`;
        window.history.replaceState({}, '', `${newPath}${window.location.search}`);
      }
      if (opts.copy && typeof navigator !== 'undefined' && navigator.clipboard) {
        const newLink = `${window.location.origin}${window.location.pathname.includes(newToken) ? window.location.pathname : window.location.pathname.replace(rawId, newToken)}${window.location.search}`;
        navigator.clipboard.writeText(newLink).catch(() => {});
      }
      // Update display immediately
      setTokenRemainingPublic('72 jam 0 menit');
      setErrorDialog({ open: true, title: 'Link Baru Dibuat', message: 'Link publik baru dibuat dan disalin ke clipboard. Bagikan link ini ke pemberi akses.', type: 'success' });
    } catch (e) {
      // still set token locally
      setErrorDialog({ open: true, title: 'Link Baru Dibuat', message: 'Link publik baru dibuat. Salin secara manual dari address bar jika perlu.', type: 'success' });
    }
  };

  const handleFileChange = (docId, event) => {
    const file = event.target.files[0];
    if (isPublicLinkExpired) {
      Swal.fire({
        ...SwalConfig.info,
        title: 'Link Kadaluwarsa',
        text: 'Link publik telah kadaluwarsa. Silakan buat link baru atau minta HRD untuk membuat link baru.'
      });
      return;
    }
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          ...SwalConfig.error,
          title: 'Format Salah',
          text: 'Harap unggah file PDF atau Gambar (JPG/PNG).'
        });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          ...SwalConfig.error,
          title: 'File Terlalu Besar',
          text: 'Maksimal ukuran file adalah 5MB.'
        });
        return;
      }

      setFiles(prev => ({ ...prev, [docId]: file }));
      // Reset status if re-selecting
      setUploadStatus(prev => ({ ...prev, [docId]: null }));
      // Immediately start upload when a valid file is selected
      // Small timeout to allow state propagation
      setTimeout(() => uploadSingleFile(docId), 50);
    }
  };

  // Helper: normalize text for matching
  const normalize = (str) => (str || '').toLowerCase().replace(/[^a-z0-9]+/g,' ').trim();
  const existingTokens = React.useMemo(() => {
    const tokens = new Set();
    existingDocs.forEach(d => {
      [d.document_name, d.category_name].forEach(val => {
        normalize(val).split(' ').forEach(tok => { if (tok) tokens.add(tok); });
      });
    });
    return tokens;
  }, [existingDocs]);

  const labelMatchesExisting = (label) => {
    const words = normalize(label).split(' ');
    let hit = 0;
    words.forEach(w => { if (existingTokens.has(w)) hit++; });
    return hit >= 2; // require at least 2 token overlaps to reduce false positives
  };

  // Total documents we track for progress is the number of known document types
  const expectedTotal = documentTypes.length;

  // Compute how many of the tracked document types are present (either already in existingDocs
  // or have just been uploaded successfully). This makes progress reflect per-type coverage
  // instead of raw number of files in existingDocs which may contain unrelated items.
  const uploadedCount = React.useMemo(() => {
    return documentTypes.reduce((acc, doc) => {
      const normLabel = normalize(sanitizeLabel(doc.label));
      const isExisting = existingDocs.some(d => {
        const docNameNorm = normalize(sanitizeLabel(d.document_name || ''));
        const categoryNorm = normalize(sanitizeLabel(d.category_name || ''));
        return (
          docNameNorm.includes(normLabel) ||
          normLabel.includes(docNameNorm) ||
          categoryNorm.includes(normLabel) ||
          normLabel.includes(categoryNorm)
        );
      });
      const justUploaded = uploadStatus[doc.id] === 'success';
      return acc + (isExisting || justUploaded ? 1 : 0);
    }, 0);
  }, [existingDocs, uploadStatus]);

  const progressPercent = Math.min(100, Math.round((uploadedCount / expectedTotal) * 100));

  const uploadSingleFile = async (docId) => {
    if (isPublicLinkExpired) {
      setErrorDialog({ open: true, title: 'Link Kadaluwarsa', message: 'Maaf, link publik telah kadaluwarsa. Silakan buat link baru sebelum mengunggah dokumen.', type: 'error' });
      return;
    }
    const file = files[docId];
    const docType = documentTypes.find(d => d.id === docId);
    
    if (!file || !docType) return;
    const reupload = labelMatchesExisting(docType.label);

    setUploadStatus(prev => ({ ...prev, [docId]: 'uploading' }));
    setUploadProgress(prev => ({ ...prev, [docId]: 0 }));

    const sanitizedLabel = sanitizeLabel(docType.label);
    const originalExt = file.name.includes('.') ? file.name.split('.').pop() : (file.type === 'application/pdf' ? 'pdf' : '');
    const backendFileName = originalExt ? `${sanitizedLabel}.${originalExt}` : sanitizedLabel;

    // Bentuk payload persis seperti yang diinginkan backend
    const folder = `${employee.full_name || 'Unknown'}-${employee.emp_id || baseEmployeeId}`;
    const gsUri = `gs://hris-eureka-documents/${folder}/${backendFileName}`;

    const payload = {
      success: true,
      filename: backendFileName,
      folder,
      gsUri
    };

    const publicToken = `${baseEmployeeId}_${Date.now()}_${Math.random().toString(36).slice(2,11)}`; // hanya dipakai lokal untuk kode dokumen

    try {
      // Simulate progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const curr = prev[docId] || 0;
          return curr < 90 ? curr + 10 : curr;
        });
      }, 200);

      const response = await fetch('https://apidashboard.eurekagroup.id/documents/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      clearInterval(interval);
      setUploadProgress(prev => ({ ...prev, [docId]: 100 }));

      let jsonResp = null;
      try {
        jsonResp = await response.clone().json();
      } catch (_) {
        jsonResp = null;
      }
      if (!response.ok || (jsonResp && jsonResp.success === false)) {
        const msg = jsonResp?.message || 'Upload failed';
        throw new Error(msg);
      }

      setUploadStatus(prev => ({ ...prev, [docId]: 'success' }));

      // Integrate returned payload directly if format matches expected ({success, filename, folder, gsUri})
      if (jsonResp && jsonResp.success && jsonResp.filename) {
        setExistingDocs(prev => {
          const updated = [...prev];
          // Avoid duplicate if a doc with same sanitized name exists
          const alreadyIndex = updated.findIndex(d => sanitizeLabel(d.document_name || '').toLowerCase() === sanitizedLabel.toLowerCase());
          const newDoc = {
            document_name: sanitizedLabel,
            filename: jsonResp.filename,
            folder: jsonResp.folder,
            gsUri: jsonResp.gsUri,
            date_added: dayjs().format('YYYY-MM-DD'),
            document_code: publicToken.slice(-8),
            category_name: sanitizedLabel,
            full_name: employee?.full_name || 'User'
          };
          if (alreadyIndex >= 0) {
            updated[alreadyIndex] = { ...updated[alreadyIndex], ...newDoc }; // overwrite
          } else {
            updated.push(newDoc);
          }
          return updated;
        });
        // Clear selected file so UI prefers 'existing' state instead of showing selected file
        setFiles(prev => {
          const copy = { ...prev };
          if (Object.prototype.hasOwnProperty.call(copy, docId)) delete copy[docId];
          return copy;
        });
      } else {
        // If backend didn't return document metadata but file exists in GCS, try to read GCS listing
        try {
          const listResp = await fetch(`https://apidashboard.eurekagroup.id/documents/list-gcs?folder=${encodeURIComponent(folder)}`);
          if (listResp.ok) {
            const listJson = await listResp.json().catch(() => null);
            if (listJson && Array.isArray(listJson.items) && listJson.items.length > 0) {
              // try to find a matching filename; prefer exact or contains sanitized base
              const targetNorm = normalize(sanitizedLabel);
              const targetTokens = targetNorm.split(' ').filter(Boolean);
              // Prefer items where filename shares at least one token with target label
              let chosen = listJson.items.find(it => {
                const fn = normalize(it.filename || '');
                const fnTokens = fn.split(' ').filter(Boolean);
                return targetTokens.some(t => fnTokens.includes(t));
              });
              if (!chosen) chosen = listJson.items[listJson.items.length - 1]; // fallback to last uploaded

              setExistingDocs(prev => {
                const updated = [...prev];
                const alreadyIndex = updated.findIndex(d => sanitizeLabel(d.document_name || '').toLowerCase() === sanitizedLabel.toLowerCase());
                const newDoc = {
                  document_name: sanitizedLabel,
                  filename: chosen.filename,
                  folder: listJson.folder || folder,
                  gsUri: chosen.gsUri || `gs://hris-eureka-documents/${folder}/${chosen.filename}`,
                  date_added: dayjs().format('YYYY-MM-DD'),
                  document_code: publicToken.slice(-8),
                  category_name: sanitizedLabel,
                  full_name: employee?.full_name || 'User'
                };
                if (alreadyIndex >= 0) {
                  updated[alreadyIndex] = { ...updated[alreadyIndex], ...newDoc };
                } else {
                  updated.push(newDoc);
                }
                return updated;
              });
              // Clear selected file so UI prefers 'existing' state instead of showing selected file
              setFiles(prev => {
                const copy = { ...prev };
                if (Object.prototype.hasOwnProperty.call(copy, docId)) delete copy[docId];
                return copy;
              });
            } else {
              // fallback: try to refresh employee documents
              const ref = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${baseEmployeeId}`);
              if (ref.ok) {
                const fresh = await ref.json();
                if (fresh.documents) setExistingDocs(fresh.documents);
              }
            }
          }
        } catch (e) {
          if (debugMode) console.log('list-gcs/refetch error', e);
          try {
            const ref = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${baseEmployeeId}`);
            if (ref.ok) {
              const fresh = await ref.json();
              if (fresh.documents) setExistingDocs(fresh.documents);
            }
          } catch(_){/* ignore */}
        }
      }

      setErrorDialog({
        open: true,
        title: reupload ? '✅ Versi Baru Diupload!' : '✨ Berhasil Upload!',
        message: reupload 
          ? `${docType.label} berhasil diperbarui. Versi terbaru sudah tercatat.`
          : `${docType.label} berhasil diunggah dengan sempurna.`,
        type: 'success'
      });

    } catch (error) {
      console.error(`Error uploading ${docType.label}:`, error);
      setUploadStatus(prev => ({ ...prev, [docId]: 'error' }));
      
      // Show modern error modal
      setErrorDialog({
        open: true,
        title: '❌ Upload Gagal',
        message: error.message || `Gagal mengunggah ${docType.label}. Silakan coba lagi.`,
        type: 'error'
      });
    }
  };

  // Upload helper for custom files (Dan Lain Lain)
  const uploadCustomFiles = async (customList) => {
    if (!employee) return;
    if (isPublicLinkExpired) {
      setErrorDialog({ open: true, title: 'Link Kadaluwarsa', message: 'Link publik telah kadaluwarsa. Silakan buat link baru sebelum mengunggah dokumen.', type: 'error' });
      return;
    }
    for (const item of customList) {
      const file = item.file;
      const label = item.name || 'custom';
      if (!file) continue;
      const sanitizedLabel = sanitizeLabel(label);
      const originalExt = file.name.includes('.') ? file.name.split('.').pop() : (file.type === 'application/pdf' ? 'pdf' : '');
      const backendFileName = originalExt ? `${sanitizedLabel}.${originalExt}` : sanitizedLabel;
      const folder = `${employee.full_name || 'Unknown'}-${employee.emp_id || baseEmployeeId}`;
      const gsUri = `gs://hris-eureka-documents/${folder}/${backendFileName}`;

      // simulate progress as in uploadSingleFile
      setUploadProgress(prev => ({ ...prev, [sanitizedLabel]: 0 }));
      const interval = setInterval(() => {
        setUploadProgress(prev => ({ ...prev, [sanitizedLabel]: Math.min(90, (prev[sanitizedLabel] || 0) + 10) }));
      }, 200);

      try {
        const payload = { success: true, filename: backendFileName, folder, gsUri };
        const response = await fetch('https://apidashboard.eurekagroup.id/documents/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        clearInterval(interval);
        setUploadProgress(prev => ({ ...prev, [sanitizedLabel]: 100 }));
        const jsonResp = await response.clone().json().catch(() => null);

        if (!response.ok || (jsonResp && jsonResp.success === false)) {
          throw new Error(jsonResp?.message || 'Upload failed');
        }

        // Add to existingDocs
        setExistingDocs(prev => {
          const updated = [...prev];
          updated.push({
            document_name: sanitizedLabel,
            filename: backendFileName,
            folder,
            gsUri,
            date_added: dayjs().format('YYYY-MM-DD'),
            document_code: Math.random().toString(36).slice(-8),
            category_name: sanitizedLabel,
            full_name: employee?.full_name || 'User'
          });
          return updated;
        });

      } catch (err) {
        console.error('Custom upload failed', err);
        setErrorDialog({ open: true, title: 'Upload Gagal', message: err.message || 'Gagal mengunggah file custom', type: 'error' });
      }
    }
    // close modal after uploads
    setCustomModalOpen(false);
    setCustomFilesState([{ name: '', file: null }]);
  };

  const handleUploadAll = async () => {
    if (isPublicLinkExpired) {
      Swal.fire({
        ...SwalConfig.info,
        title: 'Link Kadaluwarsa',
        text: 'Link publik telah kadaluwarsa. Silakan buat link baru sebelum mengunggah.'
      });
      return;
    }
    const filesToUpload = Object.keys(files).filter(key => files[key] && uploadStatus[key] !== 'success');
    
    if (filesToUpload.length === 0) {
      Swal.fire({
        ...SwalConfig.info,
        title: 'Tidak ada file',
        text: 'Silakan pilih file terlebih dahulu atau semua file sudah terunggah.'
      });
      return;
    }

    let successCount = 0;
    for (const docId of filesToUpload) {
      await uploadSingleFile(parseInt(docId));
      // Check status after upload attempt (we can't easily check return value of async void function without refactoring, 
      // but state updates are async. For simplicity in this loop, we just proceed)
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error">Data Karyawan Tidak Ditemukan</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f4f6f8', 
      pb: 10,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <CitySkyline />
      
      {/* Header Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)', 
        color: 'white', 
        pt: 6, 
        pb: 8, 
        px: 2,
        mb: -4,
        borderBottomLeftRadius: 40,
        borderBottomRightRadius: 40,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar 
                sx={{ 
                  width: 80, 
                  height: 80, 
                  bgcolor: 'white', 
                  color: '#1565c0',
                  fontSize: '2rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}
              >
                {employee.full_name?.charAt(0) || <PersonIcon />}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Portal Dokumen Karyawan
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {employee.full_name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<BadgeIcon sx={{ color: 'white !important' }} />} 
                  label={employee.emp_id || 'No ID'} 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
                <Chip 
                  icon={<BusinessIcon sx={{ color: 'white !important' }} />} 
                  label={employee.id_bu?.bu_name || 'Unit Bisnis'} 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
                <Chip 
                  icon={<AssignmentIcon sx={{ color: 'white !important' }} />} 
                  label={employee.id_designation?.designation_name || 'Posisi'} 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Progress Summary */}
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                icon={<DescriptionIcon />} 
                label={`${uploadedCount} / ${expectedTotal} Dokumen`} 
                color={uploadedCount === expectedTotal ? 'success' : 'primary'}
                sx={{ fontWeight: 600 }}
              />
              <Chip 
                icon={<WarningIcon />} 
                label="Link berlaku 72 jam" 
                variant="outlined" 
                color="info"
              />
              {tokenRemainingPublic && (
                <Chip
                  icon={<ClockIcon sx={{ color: 'white !important' }} />}
                  label={`Sisa waktu: ${tokenRemainingPublic}`}
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 1 }}
                />
              )}
              {isPublicLinkExpired && (
                <Button variant="outlined" size="small" onClick={() => generateNewPublicToken()} sx={{ ml: 1 }}>
                  Buat Link Baru
                </Button>
              )}
              {uploadedCount === expectedTotal && (
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="Lengkap" 
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progressPercent} 
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Typography variant="caption" sx={{ textAlign: 'right', fontWeight: 500 }}>
              Progress {progressPercent}%
            </Typography>
            <Alert severity="info" icon={<WarningIcon />} sx={{ borderRadius: 2, mt: 1 }}>
              Silakan upload semua dokumen yang diperlukan. Dokumen bertanda <strong>Wajib</strong> harus diunggah. Format yang diterima: PDF, JPG, JPEG, PNG.
            </Alert>
          </Box>
        </Card>
        {/* Existing Documents Section */}
        {existingDocs.length > 0 && (
          <Card 
            sx={{ 
              mb: 4, 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: 'primary.main', 
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setShowExistingDocs(!showExistingDocs)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'white', color: 'primary.main' }}>
                  <FileIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Dokumen Yang Sudah Ada
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {existingDocs.length} dokumen telah terupload
                  </Typography>
                </Box>
              </Box>
              <IconButton sx={{ color: 'white' }}>
                {showExistingDocs ? '▲' : '▼'}
              </IconButton>
            </Box>
            
            <Collapse in={showExistingDocs}>
              <CardContent sx={{ p: 0 }}>
                <List sx={{ py: 0 }}>
                  {existingDocs.map((doc, index) => (
                    <React.Fragment key={index}>
                      <ListItem 
                        sx={{ 
                          py: 2,
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'success.light', width: 40, height: 40 }}>
                            <CheckCircleOutlineIcon sx={{ color: 'white' }} />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight="bold">
                              {doc.document_name}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip 
                                label={doc.category_name || 'No Category'} 
                                size="small" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              <Chip 
                                label={`Kode: ${doc.document_code}`} 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              <Chip 
                                label={`Upload: ${dayjs(doc.date_added).format('DD MMM YYYY')}`} 
                                size="small" 
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {doc.full_name && (
                                <Chip 
                                  label={`By: ${doc.full_name}`} 
                                  size="small" 
                                  sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'grey.200' }}
                                />
                              )}
                            </Box>
                          }
                        />
                        <IconButton aria-label="lihat" onClick={() => openDoc(doc.document_name)}>
                          <VisibilityIcon />
                        </IconButton>
                      </ListItem>
                      {index < existingDocs.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Collapse>
          </Card>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            Daftar Dokumen
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadAll}
            size="large"
            sx={{ borderRadius: 2, px: 4, boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)' }}
            disabled={isPublicLinkExpired}
          >
            Upload Semua File Terpilih
          </Button>
        </Box>

        <Grid container spacing={3}>
          {documentTypes.map(doc => {
            // Determine if this document (by label or matching keywords) already exists for employee
            // Use the same normalization utility used elsewhere (normalize) to avoid mismatches
            // caused by characters like '/', punctuation, or different spacing/casing.
            const normLabel = normalize(sanitizeLabel(doc.label));
            const isExisting = existingDocs.some(d => {
              const docNameNorm = normalize(sanitizeLabel(d.document_name || ''));
              const categoryNorm = normalize(sanitizeLabel(d.category_name || ''));
              return (
                docNameNorm.includes(normLabel) ||
                normLabel.includes(docNameNorm) ||
                categoryNorm.includes(normLabel) ||
                normLabel.includes(categoryNorm)
              );
            });
            return (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card 
                  sx={{ 
                    position: 'relative',
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                    },
                    border: isExisting ? '2px solid #2e7d32' : (uploadStatus[doc.id] === 'success' ? '2px solid #4caf50' : '1px solid #e0e0e0')
                  }}
                >
                  {isExisting && (
                    <Chip 
                      label="Sudah Ada" 
                      size="small" 
                      color="success" 
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8, 
                        fontSize: '0.625rem', 
                        fontWeight: 600, 
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Avatar sx={{ bgcolor: (uploadStatus[doc.id] === 'success' || isExisting) ? 'success.light' : 'primary.light', color: 'white' }}>
                        {(uploadStatus[doc.id] === 'success' || isExisting) ? <CheckCircleIcon /> : doc.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                          {doc.label}
                        </Typography>
                        {doc.required && (
                          <Chip label="Wajib" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                        {doc.link && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            <a href={doc.link} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                              Link Tes DISC
                            </a>
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      <input
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        id={`file-input-${doc.id}`}
                        type="file"
                        disabled={isPublicLinkExpired}
                        onChange={(e) => handleFileChange(doc.id, e)}
                      />
                      <label htmlFor={`file-input-${doc.id}`} style={{ width: '100%' }}>
                        <Button
                          variant={files[doc.id] ? 'outlined' : 'dashed'}
                          component="span"
                          fullWidth
                          color={files[doc.id] ? 'primary' : (isExisting ? 'success' : 'inherit')}
                          sx={{ 
                            borderStyle: files[doc.id] ? 'solid' : 'dashed',
                            borderColor: files[doc.id] ? 'primary.main' : (isExisting ? 'success.light' : 'grey.400'),
                            py: 1.5,
                            justifyContent: 'flex-start',
                            textTransform: 'none'
                          }}
                          startIcon={files[doc.id] ? <DescriptionIcon /> : <CloudUploadIcon />}
                        >
                          <Typography noWrap variant="body2">
                            {files[doc.id]
                              ? doc.label
                              : isExisting
                                ? 'Ganti File...'
                                : 'Pilih File...'}
                          </Typography>
                        </Button>
                      </label>

                      {uploadStatus[doc.id] === 'uploading' && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress variant="determinate" value={uploadProgress[doc.id] || 0} />
                        </Box>
                      )}

                      {files[doc.id] && uploadStatus[doc.id] !== 'success' && uploadStatus[doc.id] !== 'uploading' && (
                        <Button 
                          fullWidth 
                          variant="contained" 
                          size="small" 
                          color={isExisting ? 'success' : 'primary'}
                          sx={{ mt: 1 }}
                          onClick={() => uploadSingleFile(doc.id)}
                          disabled={isPublicLinkExpired}
                        >
                          {isExisting ? 'Upload Versi Baru' : 'Upload'}
                        </Button>
                      )}

                      {isExisting && (
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={() => openDoc(doc.label)}
                        >
                          Lihat Dokumen
                        </Button>
                      )}
                      
                      {(uploadStatus[doc.id] === 'success' || isExisting) && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
                          <CheckCircleIcon fontSize="small" /> {uploadStatus[doc.id] === 'success' && isExisting ? 'Versi baru terupload' : (isExisting ? 'Sudah Ada' : 'Terupload')}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {/* Dan Lain-Lain card (custom multi-file modal) */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', color: 'white' }}>
                    <FileIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                      Dan Lain Lain
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Upload dokumen lain-lain. Bisa tambahkan beberapa file dan beri nama sesuai kebutuhan.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 'auto' }}>
                  <Button fullWidth variant="outlined" onClick={() => setCustomModalOpen(true)} disabled={isPublicLinkExpired}>
                    Upload Dokumen: Dan Lain Lain
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Helper functions for opening existing docs */}
      {/* Placed near bottom to avoid hoisting issues; could be moved above component return if preferred */}
      {/* openDoc uses existingDocs state */}
      {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
      {false && <div />}

      {/* Modern Error/Success Dialog */}
      <Dialog 
        open={errorDialog.open} 
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          pt: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar 
            sx={{ 
              bgcolor: errorDialog.type === 'success' ? 'success.light' : 'error.light',
              width: 56,
              height: 56
            }}
          >
            {errorDialog.type === 'success' ? (
              <CheckCircleIcon sx={{ fontSize: 32, color: 'white' }} />
            ) : (
              <WarningIcon sx={{ fontSize: 32, color: 'white' }} />
            )}
          </Avatar>
          <Typography variant="h5" fontWeight="bold" sx={{ flex: 1 }}>
            {errorDialog.title}
          </Typography>
          <IconButton 
            onClick={() => setErrorDialog({ ...errorDialog, open: false })}
            sx={{ 
              color: 'grey.500',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          <Alert 
            severity={errorDialog.type === 'success' ? 'success' : 'error'}
            variant="outlined"
            sx={{ 
              mt: 2,
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {errorDialog.message}
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setErrorDialog({ ...errorDialog, open: false })}
            variant="contained"
            color={errorDialog.type === 'success' ? 'success' : 'error'}
            size="large"
            fullWidth
            sx={{ 
              borderRadius: 2,
              py: 1.5,
              fontWeight: 'bold',
              boxShadow: errorDialog.type === 'success' 
                ? '0 4px 12px rgba(76, 175, 80, 0.3)' 
                : '0 4px 12px rgba(244, 67, 54, 0.3)'
            }}
          >
            {errorDialog.type === 'success' ? '✨ OK, Mengerti!' : '🔄 OK, Saya Coba Lagi'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Custom Upload Modal for Dan Lain Lain */}
      <Dialog open={customModalOpen} onClose={() => setCustomModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Dokumen: Dan Lain Lain</DialogTitle>
        <DialogContent>
          {customFilesState.map((cf, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
              <TextField
                label={`Nama File ${idx + 1}`}
                value={cf.name}
                onChange={(e) => setCustomFilesState(prev => prev.map((p, i) => i === idx ? { ...p, name: e.target.value } : p))}
                fullWidth
                size="small"
              />
              <Button component="label" variant="outlined" size="small">
                Pilih File
                <input type="file" hidden onChange={(e) => setCustomFilesState(prev => prev.map((p, i) => i === idx ? { ...p, file: e.target.files[0] } : p))} />
              </Button>
              {customFilesState.length > 1 && (
                <IconButton onClick={() => setCustomFilesState(prev => prev.filter((_, i) => i !== idx))}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          ))}
          <Button variant="text" onClick={() => setCustomFilesState(prev => [...prev, { name: '', file: null }])}>+ tambah file</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomModalOpen(false)}>Batal</Button>
          <Button variant="contained" onClick={() => uploadCustomFiles(customFilesState)}>Upload Semua</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicEmployeeDocs;
